'use client';
import logger from '@/utils/client-logger';
import { useTranslations } from '@/i18n/use-translations';
import { useSpmDomAttr } from '@/reporting/spm';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FreeMode, Navigation } from 'swiper/modules';
import { Swiper, SwiperClass, SwiperSlide } from 'swiper/react';
import { getSpmPositionCode, useProductDetailSpmCodes } from '../hooks/useGoodsDetailTrack';
import RelationItemComponent, { RelationItem as RelationItemInterface } from './RelationItem';
import SwiperNavigationButtons from './RelationsSwiperNavigation';

interface RelationsProps {
  list: any[];
  product: any;
  onRelatedClick?: (item: any, index: number) => void;
  onRelatedExposure?: (item: any, index: number) => void;
  isWeb?: boolean;
  className?: string;
}

// 子组件使用的接口，不包含isWeb
interface RelationsSubComponentProps {
  list: RelationItemInterface[];
  product: any;
  onRelatedClick?: (item: RelationItemInterface, index: number) => void;
  onRelatedExposure?: (item: RelationItemInterface, index: number) => void;
  className?: string;
  /** 楼层容器 Data SPM DOM 打标属性：颜色变体模块曝光编码（color_variants.code，C 位）。 */
  moduleDomSpm?: { 'data-spm': string };
}

// 移动端Relations组件
const MobileRelations: React.FC<RelationsSubComponentProps> = ({
  list,
  product,
  onRelatedClick,
  onRelatedExposure,
  className,
  moduleDomSpm,
}) => {
  const t = useTranslations('goodsDetail');
  const swiperRef = useRef<any>(null);
  const [swiperInit, setSwiperInit] = useState(false);

  // 自动滚动到当前商品
  useEffect(() => {
    if (swiperRef.current && list.length > 0) {
      try {
        const index = list.findIndex((item) => item.id === product.id);
        if (index > 4) {
          setTimeout(() => {
            if (swiperRef.current?.swiper) {
              swiperRef.current.swiper.slideTo(index, 500, false);
            }
          }, 100);
        }
      } catch (error) {
        logger.error('Auto scroll error:', error);
      }
    }
  }, [list, product.id]);

  // 移动端不需要额外的方法，都已经移到RelationItem组件中

  return (
    <div className="w-screen transform translate-x-[-14px]" {...moduleDomSpm}>
      <nav
        className={`pt-[16px] pb-[5px] ${className || ''}`}
        aria-label={t('relationColorsGroup')}
      >
        <Swiper
          ref={swiperRef}
          modules={[FreeMode]}
          slidesPerView="auto"
          freeMode
          spaceBetween={8}
          slidesOffsetBefore={14}
          slidesOffsetAfter={14}
          observer
          observeParents
          className={`relate-swiper ${swiperInit ? '' : 'translate-x-[14px]'} `}
          onInit={() => {
            setSwiperInit(true);
          }}
        >
          {list.map((item: RelationItemInterface, index) => (
            <SwiperSlide key={`${item.id}`} className="!w-auto mr-[8px]">
              <RelationItemComponent
                item={item}
                product={product}
                index={index}
                onRelatedClick={onRelatedClick}
                onRelatedExposure={onRelatedExposure}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </nav>
    </div>
  );
};

// PC端Relations组件 - 参考Relations.vue
const PcRelations: React.FC<RelationsSubComponentProps> = ({
  list,
  product,
  onRelatedClick,
  onRelatedExposure,

  className: _className, // PC端不需要，但接口中定义了，接收但不使用
  moduleDomSpm,
}) => {
  const t = useTranslations('goodsDetail');
  const swiperRef = useRef<any>(null);
  const [navigationState, setNavigationState] = useState<{
    isFirstPage: boolean;
    isLastPage: boolean;
  }>({
    isFirstPage: true,
    isLastPage: false,
  });

  // 分组逻辑 - 每组12个商品
  const divideArrayBy = (arr: any[], size: number) => {
    const result = [];
    for (let i = 0; i < arr.length; i += size) {
      result.push(arr.slice(i, i + size));
    }
    return result;
  };

  // 使用 useMemo 在下次渲染时保持记忆，避免触发下面的 useEffect
  const groupedList = useMemo(() => divideArrayBy(list, 12), [list]);

  useEffect(() => {
    try {
      let targetIndex = 0;
      for (let i = 0; i < groupedList.length; i++) {
        const groupArr = groupedList[i];
        for (let j = 0; j < groupArr.length; j++) {
          const item = groupArr[j];
          if (item.id === product.id) {
            targetIndex = i;
            break;
          }
        }
      }
      if (targetIndex > 0 && swiperRef.current?.swiper) {
        setTimeout(() => {
          swiperRef.current.swiper.slideTo(targetIndex, 500, false);
        }, 100);
      }
    } catch (error) {
      logger.error('PC Auto scroll error:', error);
    }
  }, [groupedList, product.id]);

  // PC端不需要额外的方法，都已经移到RelationItem组件中
  const handleSlideChange = (swiper: SwiperClass) => {
    setNavigationState({
      isFirstPage: swiper.activeIndex === 0,
      isLastPage: swiper.activeIndex === groupedList.length - 1,
    });
  };
  const { isFirstPage, isLastPage } = navigationState;
  return (
    <div className="relation relative box-border pt-10 pb-[1px]" {...moduleDomSpm}>
      <nav
        className="relation-content w-full py-[10px_0_1px] box-border swiper-nav-container relative"
        aria-label={t('relationColorsGroup')}
      >
        {list.length > 12 && (
          <SwiperNavigationButtons
            navigationPrefix="relation"
            disablePrev={isFirstPage}
            disableNext={isLastPage}
          />
        )}
        <Swiper
          ref={swiperRef}
          modules={[Navigation]}
          slidesPerView={1}
          spaceBetween={8}
          navigation={{
            nextEl: '.relation-next',
            prevEl: '.relation-prev',
          }}
          observer
          observeParents
          className="relate-swiper"
          onSlideChange={handleSlideChange}
        >
          {groupedList.map((groupItems, groupIndex) => (
            <SwiperSlide key={groupIndex} className="swiper-group-item">
              <div className="grid grid-cols-6 gap-[8px]">
                {groupItems.map((item: RelationItemInterface, index) => (
                  <RelationItemComponent
                    key={item.id}
                    item={item}
                    product={product}
                    index={groupIndex * 12 + index}
                    position={groupIndex * 12 + index}
                    isWeb
                    onRelatedClick={onRelatedClick}
                    onRelatedExposure={onRelatedExposure}
                  />
                ))}
              </div>
            </SwiperSlide>
          ))}
        </Swiper>

        {/* 导航按钮 */}
      </nav>
    </div>
  );
};

// 主组件
const Relations: React.FC<RelationsProps> = (props) => {
  const { isWeb, ...subComponentProps } = props;
  // 颜色变体子项的点击/曝光消费 D 位时，保留对应模块 C 位范围；无消费者时不挂标。
  const codes = useProductDetailSpmCodes(Boolean(isWeb));
  const moduleDomSpm = useSpmDomAttr(
    props.onRelatedClick || props.onRelatedExposure
      ? getSpmPositionCode(codes.color_variants, 'code')
      : undefined,
  );
  if (isWeb) {
    return <PcRelations {...subComponentProps} moduleDomSpm={moduleDomSpm} />;
  }
  return <MobileRelations {...subComponentProps} moduleDomSpm={moduleDomSpm} />;
};

export default Relations;
